import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import AccountShell from "./AccountShell";
import {
  creditAmountTone,
  fetchCreditLedgerBalance,
  fetchProfileActivity,
  fetchUserCreditTransactions,
  fetchUserEvaluations,
  filterEvaluationsByUserId,
  formatActivityDate,
  formatCreditAmount,
  resolveAvailableCredits,
  resolveLastEvaluation,
} from "../../utils/accountActivity";
import {
  formatAuthDate,
  getAuthCredits,
  getAuthLastEvaluation,
  getAuthToken,
  getAuthUserId,
} from "../../utils/userIdentity";
import { scrollTop } from "../../utils/window";
import "./account.css";

const PAGE_SIZE = 10;

function CreditsPage({ auth }) {
  const history = useHistory();
  const userId = getAuthUserId(auth);
  const token = getAuthToken(auth);
  const authCredits = useMemo(() => getAuthCredits(auth), [auth]);
  const authLastEvaluation = useMemo(() => getAuthLastEvaluation(auth), [auth]);
  const [credits, setCredits] = useState(authCredits);
  const [lastEvaluation, setLastEvaluation] = useState(authLastEvaluation);
  const [creditHistory, setCreditHistory] = useState([]);
  const [creditCount, setCreditCount] = useState(0);
  const [evaluations, setEvaluations] = useState([]);
  const [historyNote, setHistoryNote] = useState("");
  const [evaluationsNote, setEvaluationsNote] = useState("");
  const [loading, setLoading] = useState(Boolean(userId && token));
  const [txLoading, setTxLoading] = useState(false);
  const [error, setError] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    scrollTop();
  }, []);

  useEffect(() => {
    setCredits(authCredits);
    setLastEvaluation(authLastEvaluation);
  }, [authCredits, authLastEvaluation]);

  // Profile + evaluations once
  useEffect(() => {
    if (!userId || !token) {
      setLoading(false);
      if (authCredits == null) {
        setError("Please log in to view credit information.");
      }
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError("");
    setEvaluationsNote("");

    Promise.allSettled([
      fetchProfileActivity({ userId, token }),
      fetchUserEvaluations({ token, userId }),
      fetchCreditLedgerBalance({ token, userId, pageSize: 100 }),
    ])
      .then(([profileResult, evaluationsResult, ledgerResult]) => {
        if (cancelled) return;
        const profileCredits =
          profileResult.status === "fulfilled"
            ? profileResult.value.credits
            : null;
        const ledgerRows =
          ledgerResult.status === "fulfilled"
            ? ledgerResult.value.results || []
            : [];
        const ledgerBalance =
          ledgerResult.status === "fulfilled"
            ? ledgerResult.value.balance
            : null;
        const nextCredits = resolveAvailableCredits(
          profileCredits,
          ledgerRows,
          { preferLedger: ledgerBalance != null }
        );
        if (nextCredits != null) setCredits(nextCredits);
        else if (authCredits != null) setCredits(authCredits);

        const profileLast =
          profileResult.status === "fulfilled"
            ? profileResult.value.lastEvaluation
            : "";
        let evalList = [];
        if (evaluationsResult.status === "fulfilled") {
          evalList = (evaluationsResult.value.list || []).filter((item) => {
            if (!item?.raw) return true;
            return filterEvaluationsByUserId([item.raw], userId).length > 0;
          });
          setEvaluations(evalList);
        } else {
          setEvaluationsNote("Could not load evaluations.");
        }

        const nextLast = resolveLastEvaluation(profileLast, evalList);
        if (nextLast) setLastEvaluation(nextLast);
        else if (authLastEvaluation) setLastEvaluation(authLastEvaluation);

        if (
          profileResult.status !== "fulfilled" &&
          nextCredits == null &&
          authCredits == null
        ) {
          setError("Could not load credit information right now.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userId, token, authCredits, authLastEvaluation]);

  const loadTransactions = useCallback(
    (nextPage) => {
      if (!userId || !token) return;
      setTxLoading(true);
      setHistoryNote("");
      fetchUserCreditTransactions({
        token,
        userId,
        page: nextPage,
        pageSize: PAGE_SIZE,
      })
        .then((parsed) => {
          setCreditHistory(parsed.results || []);
          setCreditCount(parsed.count || 0);
          setPage(parsed.page || nextPage);
        })
        .catch(() => {
          setCreditHistory([]);
          setCreditCount(0);
          setHistoryNote("Could not load credit history.");
        })
        .finally(() => setTxLoading(false));
    },
    [userId, token]
  );

  useEffect(() => {
    if (!userId || !token) return;
    loadTransactions(1);
  }, [userId, token, loadTransactions]);

  const filteredHistory = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (creditHistory || []).filter((row) => {
      const amount = Number(row.amount);
      if (typeFilter === "credit") {
        if (!(Number.isFinite(amount) && amount > 0)) return false;
      } else if (typeFilter === "debit") {
        if (!(Number.isFinite(amount) && amount < 0)) return false;
      }
      if (!q) return true;
      const haystack = [
        row.title,
        row.vehicle,
        row.eventTypeName,
        formatActivityDate(row.date),
        formatCreditAmount(row.amount),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [creditHistory, typeFilter, query]);

  const totalPages = Math.max(1, Math.ceil(creditCount / PAGE_SIZE));
  const formattedLastEvaluation = formatAuthDate(lastEvaluation);

  return (
    <AccountShell
      title="Credits & evaluations"
      lead="Your balance, credit ledger, and evaluations linked to your account."
    >
      {loading ? <p className="pi-account-note">Loading credits…</p> : null}
      {!loading && error ? (
        <p className="pi-account-alert pi-account-alert--err">{error}</p>
      ) : null}
      {!loading && !error ? (
        <>
          <div className="pi-account-stats">
            <div className="pi-account-stat">
              <span>Available credits</span>
              <strong>{credits == null ? "—" : credits}</strong>
            </div>
            <div className="pi-account-stat">
              <span>Last evaluation</span>
              <strong className="pi-account-stat__date">
                {formattedLastEvaluation || "—"}
              </strong>
            </div>
          </div>

          <section className="pi-account-section">
            <div className="pi-account-section__head">
              <h2>Credit history</h2>
              <span>{creditCount} total</span>
            </div>

            <div className="pi-account-filters">
              <div
                className="pi-account-filters__types"
                role="group"
                aria-label="Transaction type"
              >
                {[
                  { id: "all", label: "All" },
                  { id: "credit", label: "Credits" },
                  { id: "debit", label: "Debits" },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    className={`pi-account-filter-chip${
                      typeFilter === opt.id ? " is-active" : ""
                    }`}
                    onClick={() => setTypeFilter(opt.id)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <label className="pi-account-filters__search">
                <span className="pi-account-sr-only">Search credit history</span>
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search event, vehicle, points…"
                />
              </label>
            </div>

            {txLoading ? (
              <p className="pi-account-note">Loading transactions…</p>
            ) : filteredHistory.length ? (
              <ul className="pi-account-list">
                {filteredHistory.map((row) => (
                  <li key={row.id} className="pi-account-list__item">
                    <div>
                      <strong>{row.title}</strong>
                      <p>
                        {[
                          formatActivityDate(row.date),
                          row.vehicle,
                          row.status,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    </div>
                    <em
                      className={`pi-account-amount pi-account-amount--${creditAmountTone(
                        row.amount
                      )}`}
                    >
                      {formatCreditAmount(row.amount)}
                    </em>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="pi-account-note">
                {historyNote ||
                  (creditHistory.length
                    ? "No transactions match these filters on this page."
                    : "No credit history is available on this account yet.")}
              </p>
            )}

            {creditCount > PAGE_SIZE ? (
              <div className="pi-account-pagination">
                <button
                  type="button"
                  className="pi-account-btn pi-account-btn--ghost"
                  disabled={page <= 1 || txLoading}
                  onClick={() => loadTransactions(page - 1)}
                >
                  Previous
                </button>
                <span>
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  className="pi-account-btn pi-account-btn--ghost"
                  disabled={page >= totalPages || txLoading}
                  onClick={() => loadTransactions(page + 1)}
                >
                  Next
                </button>
              </div>
            ) : null}
          </section>

          <section className="pi-account-section">
            <div className="pi-account-section__head">
              <h2>Evaluations</h2>
              <span>{evaluations.length} total</span>
            </div>
            {evaluations.length ? (
              <ul className="pi-account-list">
                {evaluations.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      className="pi-account-list__item pi-account-list__item--btn"
                      onClick={() => history.push("/myappointment")}
                    >
                      <div>
                        <strong>{item.title}</strong>
                        <p>
                          {[item.reg, formatActivityDate(item.date), item.tracking]
                            .filter(Boolean)
                            .join(" · ") || "Open appointments"}
                        </p>
                      </div>
                      <span
                        className={`pi-account-badge pi-account-badge--${item.statusTone}`}
                      >
                        {item.statusLabel}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="pi-account-note">
                {evaluationsNote ||
                  "No evaluations found yet. Start a valuation to see them here."}
              </p>
            )}
            {evaluations.length ? (
              <button
                type="button"
                className="pi-account-link"
                onClick={() => history.push("/myappointment")}
              >
                View all appointments
              </button>
            ) : null}
          </section>
        </>
      ) : null}
    </AccountShell>
  );
}

const mapStateToProps = (state) => ({ auth: state.auth });
export default connect(mapStateToProps)(CreditsPage);
